import React, { useMemo } from 'react';
import clsx from 'clsx';
import { IntlShape, useIntl } from 'react-intl';
import {
  DEFAULT_RELEASE_KIT_LOOK,
  type ReleaseKitLook,
  type ReleaseThemeId,
} from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { RELEASE_ARTWORK_PX, releaseArtworkUrl } from '../aiReel/releaseArtworkUrl';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { themeStillVars } from '../../themes/themeStillVars';
import { songPickerMessages } from './songPickerMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './SongPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The cover frame of each theme (design t1): Dreamy round, Hype tilted, Neon a glow, else a plain card. */
const FRAME_CLASS: Readonly<Record<ReleaseThemeId, string | undefined>> = {
  clean: undefined,
  hype: styles.frameTilt,
  dreamy: styles.frameRound,
  dark: undefined,
  warm: undefined,
  retro: undefined,
  neon: styles.frameGlow,
  handmade: undefined,
};

/** The big number and the small words in the corner: "241 days to go", "24 days out", "1 yr out". */
function whenText(intl: IntlShape, days: number | null): { big: string; small: string } | null {
  if (days === null) {
    return null;
  }
  if (days > 0) {
    return { big: intl.formatNumber(days), small: intl.formatMessage(songPickerMessages.whenToGo, { days }) };
  }
  if (days === 0) {
    return {
      big: intl.formatMessage(songPickerMessages.whenToday),
      small: intl.formatMessage(songPickerMessages.whenOut),
    };
  }
  const out = -days;
  if (out >= 365) {
    return {
      big: intl.formatMessage(songPickerMessages.whenYears, { years: Math.floor(out / 365) }),
      small: intl.formatMessage(songPickerMessages.whenOut),
    };
  }
  return { big: intl.formatNumber(out), small: intl.formatMessage(songPickerMessages.whenOutDays, { days: out }) };
}

interface SongPickerHeroProps {
  item: CatalogItem;
  /** The look of the kit; null for a song with no kit (it shows Clean, the default). */
  look: ReleaseKitLook | null;
  palette: readonly string[];
  days: number | null;
  onClick: () => void;
}

/**
 * The hero of a picker card (design t1): the song in its release theme. The ground, the text and
 * accent colours and the display type come from `themeStillVars`, the same code as the theme stills.
 * A song with no artwork shows its first letters on the theme colours.
 */
const SongPickerHeroComponent: React.FC<SongPickerHeroProps> = ({ item, look, palette, days, onClick }) => {
  const intl = useIntl();
  const displayLook = look ?? DEFAULT_RELEASE_KIT_LOOK;
  const artworkUrl = releaseArtworkUrl(item, RELEASE_ARTWORK_PX.hero);
  const vars = useMemo(
    () =>
      themeStillVars({
        themeId: displayLook.themeId,
        fontOverride: displayLook.fontOverride,
        palette,
        artworkUrl,
        title: item.title,
        layout: 'card',
      }),
    [artworkUrl, displayLook.fontOverride, displayLook.themeId, item.title, palette]
  );
  const themeName = intl.formatMessage(releaseThemeMessages[displayLook.themeId].name);
  const when = whenText(intl, days);
  const artistLine =
    item.releaseDate === null
      ? intl.formatMessage(songPickerMessages.artistNoDate, { artist: item.artistName })
      : intl.formatMessage(songPickerMessages.artistOut, {
          artist: item.artistName,
          date: intl.formatDate(Date.parse(`${item.releaseDate.slice(0, 10)}T00:00:00Z`), {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC',
          }),
        });

  return (
    <button
      type="button"
      className={clsx(styles.hero, FRAME_CLASS[displayLook.themeId])}
      style={vars}
      data-theme-id={displayLook.themeId}
      aria-label={intl.formatMessage(songPickerMessages.heroAria, { title: item.title })}
      onClick={onClick}
    >
      {artworkUrl !== undefined && <i className={styles.blur} aria-hidden="true" />}
      <i className={styles.glow} aria-hidden="true" />
      <span className={styles.themeChip} data-testid="song-picker-theme">
        <i aria-hidden="true" />
        {look === null ? intl.formatMessage(songPickerMessages.themeDefault, { theme: themeName }) : themeName}
      </span>
      {when !== null && (
        <span className={styles.when} data-testid="song-picker-when">
          <b>{when.big}</b>
          {when.small}
        </span>
      )}
      <span className={styles.cover} aria-hidden="true">
        {artworkUrl === undefined ? item.title.slice(0, 2).toUpperCase() : null}
      </span>
      <span className={styles.heroText}>
        <span className={styles.heroTitle}>{item.title}</span>
        <span className={styles.heroArtist}>{artistLine}</span>
      </span>
    </button>
  );
};

export const SongPickerHero = TypedMemo(SongPickerHeroComponent);
